import { BenchmarkCategory } from '@/api/generated-catch-backend/model';
import { useGetRiskScoresV1RiskScoresGet } from '@/api/generated-catch-backend/risk-scores/risk-scores';
import { useIsEligibleForLabOrder } from '@/hooks/use-is-eligible-for-lab-order';
import { useMeControllerGetUserTasks } from '@/api/generated/default/default';
import { UserQuestionnaireType } from '@/components/decision-tree/types';
import { useAmplitude } from '@/context/me/amplitude.context';
import useQuestionnaireData from '@/hooks/use-questionnaire-data';
import useRiskFactorsSummary from '@/hooks/use-risk-factors-summary';
import useOnMount from '@/hooks/useOnMount';
import useGetMe from '@/queries/me.queries';
import { useGetQuestionnaire } from '@/queries/questionnaire.queries';
import { useTriggerPotentialRiskMutation } from '@/queries/risk-processing-status.queries';
import { useGetRisk } from '@/queries/risk.queries';
import { getRouteLoadingScreen } from '@/utils';
import { useEffect, useRef } from 'react';

// TODO: remove once we have a health data profile for users
// Helper function to extract user's sex from questionnaire data
function getUserGender(userQuestionnaire?: UserQuestionnaireType): 'male' | 'female' | null {
  if (!userQuestionnaire?.answers) {
    return null;
  }

  const sexAnswer = userQuestionnaire.answers.find((answer) => answer.surveyItemId === 'question_sex');

  if (!sexAnswer?.answer) {
    return null;
  }

  const answerData = sexAnswer?.answer;

  if (answerData.type === 'singleChoice' && answerData?.value === 'sex_male') {
    return 'male';
  } else if (answerData.type === 'singleChoice' && answerData?.value === 'sex_female') {
    return 'female';
  }

  return null;
}

export default function useDashboardController() {
  const { data: meData, isLoading: isLoadingMe, refetch: refetchMe } = useGetMe();
  const { data: tasksData, isLoading: isLoadingTasks } = useMeControllerGetUserTasks();
  const { data: riskData, isLoading: isLoadingRisk } = useGetRisk();
  const { data: riskScoresData } = useGetRiskScoresV1RiskScoresGet();
  const { isEligibleForLabOrder, labOrdersData } = useIsEligibleForLabOrder();
  const amplitude = useAmplitude();
  const name = meData?.profile?.name;

  const { data: userQuestionnaire } = useGetQuestionnaire();
  const { questionnaireStatus, assessmentProgress } = useQuestionnaireData(userQuestionnaire);
  const userGender = getUserGender(userQuestionnaire);
  const triggerRiskReprocessingMutation = useTriggerPotentialRiskMutation();
  const triggeredReprocessing = useRef<any>(null);

  // not ripping this out just yet
  const { risk, averageRiskPercentage, potentialRisk } = riskData ?? {
    risk: { overallRisk: 0 },
    averageRiskPercentage: 0,
    potentialRisk: 0,
  };

  const { benchmark, methodologyUrl, populationAverage, lifetimeRisk } = riskScoresData?.overall ?? {
    benchmark: BenchmarkCategory.Average,
    methodologyUrl: '',
    populationAverage: 0,
    lifetimeRisk: 0,
  };

  useOnMount(() => {
    refetchMe();
  });

  // Set analytics property for extreme risk (>80%)
  useEffect(() => {
    if (meData?.profile?.id && lifetimeRisk > 80) {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set('overall_risk_benchmark', 'extreme');
      identifyEvent.set('overall_risk_score', lifetimeRisk);
      amplitude.identify(identifyEvent);
    }
  }, [meData?.profile?.id, lifetimeRisk, amplitude]);

  const { riskFactorsSummary, modifiableRiskFactorCount } = useRiskFactorsSummary();

  const isLoading = isLoadingMe || isLoadingRisk || isLoadingTasks;

  if (!isLoading && potentialRisk === 0 && risk.overallRisk > 0 && !triggeredReprocessing.current) {
    triggeredReprocessing.current = true;
    triggerRiskReprocessingMutation.mutateAsync();
    window.location.href = getRouteLoadingScreen(window.location.pathname);
  }

  const chartSeries = [
    {
      data: [
        {
          x: 'Factors decreasing your risk',
          y: riskFactorsSummary.negativeImpactRiskFactorsCount,
        },
        {
          x: 'Factors increasing your risk',
          y: riskFactorsSummary.positiveImpactRiskFactorsCount,
        },
        {
          x: 'Factors you can modify',
          y: modifiableRiskFactorCount,
        },
      ],
    },
  ];

  return {
    name,
    questionnaireStatus,
    assessmentProgress,
    methodologyUrl,
    benchmark,
    populationAverage,
    lifetimeRisk,
    userGender,
    isLoading,
    // again, not removing these yet until we are certain they aren't used
    currentRisk: risk.overallRisk,
    averageRisk: averageRiskPercentage,
    potentialRisk,
    tasks: tasksData?.data,
    averageRiskPercentage,
    chartSeries,
    biologicalAge: riskScoresData?.biologicalAgeSummary.biologicalAge || undefined,
    labOrdersData,
    isEligibleForLabOrder,
  };
}
